/**
 * @file routes/(public)/forgot-password.tsx
 * @description 忘記密碼頁面路由（公開，無需認證）。
 *
 * 設計：中央卡片式表單，品牌漸層背景。
 *
 * 功能：
 * - 輸入信箱，請求發送密碼重設連結
 * - 無論信箱是否存在，均顯示相同提示（防枚舉攻擊）
 * - 成功後顯示結果畫面並提供返回登入按鈕
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Form, Input, Button, Card, Typography, Result } from 'antd';
import { message } from '@/lib/antd-app';
import { Mail, ArrowLeft } from 'lucide-react';
import { apiClient, ApiError } from '@/lib/api-client';

const { Title, Text } = Typography;

/** 路由定義 */
export const Route = createFileRoute('/(public)/forgot-password')({
  component: ForgotPasswordPage,
});

/**
 * 忘記密碼頁面。
 * 中央卡片式設計，品牌漸層背景，提交後切換至成功結果畫面。
 */
function ForgotPasswordPage() {
  /** 載入狀態 */
  const [loading, setLoading] = useState(false);
  /** 是否已成功提交（控制表單 / 結果頁切換） */
  const [submitted, setSubmitted] = useState(false);

  /**
   * 處理忘記密碼表單提交。
   * 呼叫 POST /api/auth/forgot-password，後端永遠回 ok（防枚舉），
   * 前端也永遠顯示「已發送」提示。
   */
  const handleSubmit = async (values: { email: string }) => {
    setLoading(true);
    try {
      await apiClient.post('/api/auth/forgot-password', {
        identifier: values.email,
      });
      setSubmitted(true);
      message.success('若該帳號存在，我們已發送重置連結（demo 環境請查看後端 console）');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : '發送失敗，請稍後重試';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #114f2c 0%, #1a8549 100%)',
        padding: 24,
      }}
    >
      <Card
        style={{
          width: 420,
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
        styles={{ body: { padding: 40 } }}
      >
        {/* 提交成功後顯示成功結果頁面 */}
        {submitted ? (
          <Result
            status="success"
            title="郵件已發送"
            subTitle="請查收您的信箱，點擊連結重設密碼。連結有效期為 24 小時。"
            extra={
              <a href="/login">
                <Button type="primary" size="large">
                  返回登入
                </Button>
              </a>
            }
          />
        ) : (
          <>
            {/* Logo 與標題區域 */}
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              {/* 品牌完整 logo（白色卡片背景，無需反相） */}
              <img
                src="/zhcert-logo-full.png"
                alt="ZHCERT"
                style={{
                  width: 88,
                  height: 'auto',
                  marginBottom: 16,
                  objectFit: 'contain',
                }}
              />
              <Title level={3} style={{ marginBottom: 4 }}>
                忘記密碼
              </Title>
              <Text type="secondary">請輸入您的註冊 Email，我們將發送密碼重設連結</Text>
            </div>

            {/* 忘記密碼表單 */}
            <Form layout="vertical" onFinish={handleSubmit} size="large">
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: '請輸入 Email' },
                  { type: 'email', message: '請輸入有效的 Email 地址' },
                ]}
              >
                <Input prefix={<Mail size={16} color="#999" />} placeholder="請輸入您的 Email" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  style={{ height: 44 }}
                >
                  發送重設連結
                </Button>
              </Form.Item>
            </Form>

            {/* 返回登入連結 */}
            <div style={{ textAlign: 'center' }}>
              <a
                href="/login"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  color: '#27a05a',
                  textDecoration: 'none',
                  fontSize: 14,
                }}
              >
                <ArrowLeft size={14} />
                返回登入
              </a>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
